/**
 * Camadas das embarcações no mapa. São três, e cada uma responde a uma
 * pergunta:
 *
 * - **escuta**: "aqui o AIS chega?" — a área onde houve recepção nos últimos
 *   7 dias, pintada por baixo de tudo. Sem ela, trecho de rio sem ponto se lê
 *   como "não há navegação", que é falso e é o erro de leitura mais provável
 *   desta camada;
 * - **frota**: as embarcações, agrupadas quando o zoom junta demais;
 * - **trilha**: por onde a embarcação SELECIONADA passou nas últimas 24 h.
 *
 * A trilha aparece só na selecionada de propósito. Desenhar as 75 de uma vez
 * cobriria o rio de linhas justamente onde a densidade é maior (Belém tem ~40
 * embarcações num punhado de quilômetros), e a trilha de quem está atracado é
 * um ponto — informação nenhuma, ruído bastante.
 *
 * Como nas estações, o estilo é reavaliado a cada quadro do loop: é isso que
 * faz a frota andar junto com a animação.
 */
import type { ReactNode } from 'react';
import Feature from 'ol/Feature';
import LineString from 'ol/geom/LineString';
import Point from 'ol/geom/Point';
import Polygon from 'ol/geom/Polygon';
import VectorLayer from 'ol/layer/Vector';
import Cluster from 'ol/source/Cluster';
import VectorSource from 'ol/source/Vector';
import CircleStyle from 'ol/style/Circle';
import Fill from 'ol/style/Fill';
import Icon from 'ol/style/Icon';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import Text from 'ol/style/Text';
import type { FeatureLike } from 'ol/Feature';
import { lonLatToMap } from '../map/projection';
import { comSelo } from '../map/contraste';
import { criarCache, dpr, fatorDoZoom } from '../map/escalaDoZoom';
import { Z } from '../map/zIndex';
import { useTimeStore } from '../state/time';
import { tipoDe, type Familia } from './kinds';
import { chaveSprite, estadoDe, visualDe, type Estado, type Visual } from './look';
import { familiaVisivel, useShipStore } from './store';
import { posicaoEm, trechos } from './track';
import type { Anonimo, Navio } from './types';

/** O que o popup recebe ao clicar. */
export interface NavioFeature {
  navio: Navio;
  familia: Familia;
  /** Posição no quadro exibido, quando a trilha alcança aquele instante. */
  instante: number | null;
}

/**
 * Resolução (metros por pixel) que separa "de longe" de "de perto".
 *
 * Em metros por pixel, e não em nível de zoom: a vista do AmaView tem
 * `maxResolution` própria (largura do setor / 256), então o número de zoom
 * do OpenLayers não é o do Web Mercator e enganaria. A 700 m/px, o porto de
 * Belém (~30 km de frota) ocupa ~40 px — o bastante para separar os símbolos.
 */
export const RESOLUCAO_AGRUPA = 700;

/**
 * Distância de agrupamento em px de tela.
 *
 * Os símbolos crescem com o zoom (`map/escalaDoZoom.ts`), mas o agrupamento só
 * vale de longe (`RESOLUCAO_AGRUPA`, perto do nível 5,3), onde o fator é no
 * máximo 0,9: ali o símbolo é MENOR que o de tamanho fixo de antes, e os 34 px
 * (com o mínimo de 18 entre grupos) continuam separando-os com folga.
 */
const DIST_AGRUPA = 34;

// ---------------------------------------------------------------------------
// Sprites
// ---------------------------------------------------------------------------

/**
 * Um sprite por aparência, SEM o rumo na chave: o símbolo é desenhado sempre
 * apontando ao norte e a rotação entra no `Style`. Com o rumo na chave, um
 * valor contínuo geraria um canvas por embarcação e por quadro, e o cache
 * cresceria sem teto.
 */
const sprites = criarCache<HTMLCanvasElement>('navios.sprites', 400);

/** Estilos prontos, na chave do sprite mais o rumo já quantizado em 5°. */
const estilos = criarCache<Style>('navios.estilos', 2500);

/** Seta apontando ao norte, com a base recortada — a forma clássica do AIS. */
function caminhoSeta(ctx: CanvasRenderingContext2D): void {
  ctx.beginPath();
  ctx.moveTo(0, -1);
  ctx.lineTo(0.62, 0.85);
  ctx.lineTo(0, 0.38);
  ctx.lineTo(-0.62, 0.85);
  ctx.closePath();
}

function caminhoLosango(ctx: CanvasRenderingContext2D): void {
  ctx.beginPath();
  ctx.moveTo(0, -0.8);
  ctx.lineTo(0.8, 0);
  ctx.lineTo(0, 0.8);
  ctx.lineTo(-0.8, 0);
  ctx.closePath();
}

function sprite(v: Visual, k: string): HTMLCanvasElement | null {
  const pronto = sprites.get(k);
  if (pronto) return pronto;

  const escala = dpr();
  const folga = v.larguraContorno + 1;
  const meio = v.raio + folga;
  const lado = Math.max(4, Math.ceil(meio * 2 * escala));

  const canvas = document.createElement('canvas');
  canvas.width = lado;
  canvas.height = lado;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.scale(escala, escala);
  ctx.translate(meio, meio);
  ctx.scale(v.raio, v.raio);

  if (v.forma === 'seta') caminhoSeta(ctx);
  else caminhoLosango(ctx);

  if (v.preenchimento) {
    ctx.fillStyle = v.preenchimento;
    ctx.fill();
  }
  ctx.strokeStyle = v.contorno;
  ctx.lineWidth = v.larguraContorno / v.raio;
  if (v.tracejado.length) ctx.setLineDash(v.tracejado.map((d) => d / v.raio));
  ctx.lineJoin = 'round';
  ctx.stroke();

  sprites.set(k, canvas);
  return canvas;
}

export function estiloDe(v: Visual): Style[] {
  const base = chaveSprite(v, dpr());
  const chave = `${base}|${v.rotacao.toFixed(3)}`;

  const pronto = estilos.get(chave);
  if (pronto) return [pronto];

  const img = sprite(v, base);
  if (!img) return [];

  const style = new Style({
    image: new Icon({
      img,
      anchor: [0.5, 0.5],
      opacity: v.opacidade,
      scale: 1 / dpr(),
      rotation: v.rotacao,
      // O norte do símbolo é o norte do mapa, não o da tela: a vista do
      // AmaView não gira, então isso só evita trabalho do renderizador.
      rotateWithView: false,
    }),
  });
  estilos.set(chave, style);
  return [style];
}

/**
 * Selo de um grupo: pílula com a contagem. Forma distinta de seta e losango.
 * NÃO escala com o zoom: é um rótulo (o número tem de caber e ser lido), e só
 * existe de longe.
 */
const selos = new Map<number, Style>();
function estiloGrupo(n: number): Style {
  const pronto = selos.get(n);
  if (pronto) return pronto;
  const raio = n < 10 ? 9 : n < 50 ? 11 : 13;
  const s = new Style({
    image: new CircleStyle({
      radius: raio,
      fill: new Fill({ color: 'rgba(11,18,32,0.88)' }),
      stroke: new Stroke({ color: 'rgba(244,211,94,0.9)', width: 1.5 }),
    }),
    text: new Text({
      text: String(n),
      font: '600 11px ui-sans-serif, system-ui, sans-serif',
      fill: new Fill({ color: '#eaf0f8' }),
      offsetY: 0.5,
    }),
  });
  selos.set(n, s);
  return s;
}

/**
 * O controle de atribuição do OpenLayers insere o texto como HTML. O crédito
 * das embarcações vem da REDE — diferente do crédito do CEMADEN, que é uma
 * constante do nosso código —, então ele nunca entra sem escapar.
 */
function escaparHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

/** Raio do ponto anônimo no fator 1. */
export const RAIO_ANONIMO = 3;

/** Raio do ponto anônimo na resolução do mapa. */
export function raioDoAnonimo(resolution: number | undefined): number {
  return RAIO_ANONIMO * fatorDoZoom(resolution);
}

/** Aparência de uma embarcação na resolução do mapa: o símbolo cresce com o zoom pela regra comum (`map/escalaDoZoom.ts`). */
export function visualNoZoom(
  familia: Familia,
  estado: Estado,
  heading: number | null,
  cog: number | null,
  estilo: { tamanho: number; opacidade: number },
  resolution: number | undefined,
): Visual {
  return visualDe(familia, estado, heading, cog, estilo.tamanho * fatorDoZoom(resolution), estilo.opacidade);
}

/** Ponto anônimo: círculo pequeno e vazado, sem identidade e sem direção. Um estilo por fator do zoom. */
const anonimos = criarCache<Style>('navios.anonimos', 16);
function estiloAnonimo(fator: number): Style {
  const k = String(fator);
  let s = anonimos.get(k);
  if (!s) {
    s = new Style({
      image: new CircleStyle({
        radius: RAIO_ANONIMO * fator,
        stroke: new Stroke({ color: 'rgba(200,210,225,0.75)', width: 1 }),
      }),
    });
    anonimos.set(k, s);
  }
  return s;
}

export interface ShipLayers {
  camadas: VectorLayer<VectorSource>[];
  dispose: () => void;
}

/** O que o popup de um GRUPO recebe: para oferecer "aproximar". */
export interface GrupoFeature {
  total: number;
  identificados: NavioFeature[];
  /** Coordenadas (na projeção do mapa) dos membros, para enquadrar. */
  coordenadas: number[][];
}

export function createShipLayers(
  renderPopup: (f: NavioFeature) => ReactNode | null,
  renderCluster: (g: GrupoFeature) => ReactNode | null,
): ShipLayers {
  // ---- escuta ----
  const fonteEscuta = new VectorSource();
  const camadaEscuta = new VectorLayer({
    source: fonteEscuta,
    zIndex: Z.SHIPS_RANGE,
    className: 'ol-layer amaview-ships-range',
    properties: { id: 'ships-range', title: 'Alcance do AIS' },
    // Sem contorno e com pouca tinta: as células de 0,25° se somam numa
    // mancha, e o que importa é a BORDA dela, não cada quadradinho. Forte
    // demais competiria com a imagem do satélite, que é o assunto do mapa.
    style: new Style({
      fill: new Fill({ color: 'rgba(142,202,230,0.16)' }),
    }),
    updateWhileAnimating: false,
    updateWhileInteracting: false,
  });

  // ---- trilha da selecionada ----
  const fonteTrilha = new VectorSource();
  const camadaTrilha = new VectorLayer({
    source: fonteTrilha,
    zIndex: Z.SHIPS_TRACK,
    className: 'ol-layer amaview-ships-track',
    properties: { id: 'ships-track', title: 'Trilha da embarcação' },
    style: [
      new Style({ stroke: new Stroke({ color: 'rgba(10,16,28,0.55)', width: 4 }) }),
      new Style({ stroke: new Stroke({ color: 'rgba(244,211,94,0.95)', width: 1.8 }) }),
    ],
    updateWhileAnimating: false,
    updateWhileInteracting: false,
  });

  // ---- frota ----
  //
  // Duas camadas sobre a MESMA fonte, e nunca as duas ao mesmo tempo:
  //
  // - de longe (`camadaLonge`), a fonte passa por um `Cluster`: nos portos, 40
  //   embarcações cabem em poucos pixels e viram um selo com a contagem;
  // - de perto (`camadaPerto`), a fonte é desenhada direto. Aqui o cluster
  //   seria só custo — e pior: o teste de clique devolveria o grupo, e a ficha
  //   aberta seria a de um membro qualquer, não a do navio clicado.
  //
  // A fonte contém SÓ o que está visível neste quadro. A cada quadro do loop
  // ela é esvaziada em silêncio e repovoada de uma vez: um único `change`,
  // uma única reclusterização. Mover 150 geometrias uma a uma dentro da fonte
  // disparava 150 reclusterizações por quadro — 27 ms a 5 fps, medido.
  const fonteFrota = new VectorSource();
  /** Uma feature por embarcação (e por ponto anônimo), reposicionada por quadro. */
  let todas: Feature[] = [];

  const camadaPerto = new VectorLayer({
    source: fonteFrota,
    maxResolution: RESOLUCAO_AGRUPA,
    zIndex: Z.SHIPS,
    className: 'ol-layer amaview-ships',
    properties: { id: 'ships', title: 'Embarcações (AIS)' },
    updateWhileAnimating: false,
    updateWhileInteracting: false,
  });
  camadaPerto.setStyle((feature, resolution) => estiloIndividual(feature as Feature, resolution));
  camadaPerto.set('popup', (feature: FeatureLike): ReactNode | null => {
    const d = feature.get('navio') as NavioFeature | undefined;
    return d ? renderPopup(d) : null;
  });

  const agrupada = new Cluster({ source: fonteFrota, distance: DIST_AGRUPA, minDistance: 18 });
  const camadaLonge = new VectorLayer({
    source: agrupada,
    minResolution: RESOLUCAO_AGRUPA,
    zIndex: Z.SHIPS,
    className: 'ol-layer amaview-ships-far',
    properties: { id: 'ships-far', title: 'Embarcações (AIS), agrupadas' },
    updateWhileAnimating: false,
    updateWhileInteracting: false,
  });
  camadaLonge.setStyle((feature, resolution) => {
    const membros = (feature.get('features') as Feature[] | undefined) ?? [];
    if (membros.length === 0) return [];
    if (membros.length === 1) return estiloIndividual(membros[0]!, resolution);
    return [estiloGrupo(membros.length)];
  });
  camadaLonge.set('popup', (feature: FeatureLike): ReactNode | null => {
    const membros = (feature.get('features') as Feature[] | undefined) ?? [];
    if (membros.length === 1) {
      const d = membros[0]!.get('navio') as NavioFeature | undefined;
      return d ? renderPopup(d) : null;
    }
    const identificados = membros.map((m) => m.get('navio') as NavioFeature | undefined).filter((d): d is NavioFeature => !!d);
    const coords = membros.map((m) => (m.getGeometry() as Point).getCoordinates());
    return renderCluster({ total: membros.length, identificados, coordenadas: coords });
  });

  function estiloIndividual(f: Feature, resolution?: number): Style[] {
    const anon = f.get('anonimo') as Anonimo | undefined;
    // Sobre a foto de satélite do fundo, o selo escuro por baixo (map/contraste.ts).
    if (anon) return comSelo([estiloAnonimo(fatorDoZoom(resolution))], raioDoAnonimo(resolution) + 3, resolution);
    const d = f.get('navio') as NavioFeature | undefined;
    if (!d) return [];
    const { estilo } = useShipStore.getState();
    const pos = f.get('pos') as { sog: number | null; ms: number } | null;
    // "Velha" é a EMBARCAÇÃO que não é ouvida há mais de uma hora — não o
    // quadro. Comparar o instante do quadro com o relógio pintava a frota
    // inteira de tracejado assim que o loop passava de uma hora atrás.
    const estado = estadoDe(pos?.sog ?? d.navio.sog, d.navio.visto, d.navio.sumiu);
    const v = visualNoZoom(d.familia, estado, d.navio.heading, d.navio.cog, estilo, resolution);
    return comSelo(estiloDe(v), v.raio + v.larguraContorno + 3, resolution);
  }

  // -------------------------------------------------------------------------
  // Reconstrução e sincronia
  // -------------------------------------------------------------------------

  function reconstruir(): void {
    const { dados } = useShipStore.getState();

    // Crédito no rodapé de atribuição do mapa, uma linha por fonte presente.
    // A licença do AIS é por fonte, e o aiscast é explícito ao dizer que elas
    // não se fundem — por isso não concatenamos os textos num só.
    //
    // O OpenLayers insere atribuição como HTML, e este texto vem da REDE (ao
    // contrário do crédito do CEMADEN, que é constante do nosso código). Vai
    // escapado.
    fonteFrota.setAttributions(Object.values(dados.atribuicao).map(escaparHtml));

    todas = [];
    for (const n of dados.navios) {
      const f = new Feature({ geometry: new Point([n.mx, n.my]) });
      f.set('navio', { navio: n, familia: tipoDe(n.ficha?.tipo ?? null).familia, instante: null } satisfies NavioFeature);
      todas.push(f);
    }
    for (const a of dados.anonimos) {
      const f = new Feature({ geometry: new Point([a.mx, a.my]) });
      f.set('anonimo', a);
      todas.push(f);
    }

    fonteEscuta.clear(true);
    fonteEscuta.addFeatures(
      dados.escuta.map((c) => {
        const [minLon, minLat, maxLon, maxLat] = c.caixa;
        const anel = [
          lonLatToMap(minLon, minLat),
          lonLatToMap(maxLon, minLat),
          lonLatToMap(maxLon, maxLat),
          lonLatToMap(minLon, maxLat),
          lonLatToMap(minLon, minLat),
        ].filter((p) => Number.isFinite(p[0]) && Number.isFinite(p[1]));
        return new Feature({ geometry: new Polygon([anel]) });
      }),
    );

    reposicionar();
    redesenharTrilha();
  }

  /**
   * Move cada embarcação para onde ela estava no quadro exibido, e decide
   * quem aparece (filtro de família, sumidas, camada ligada).
   *
   * Uma embarcação cuja trilha não alcança o instante do quadro é escondida:
   * mostrar a posição de agora sobre uma imagem de três horas atrás faria a
   * frota parecer parada no tempo enquanto a nuvem corre.
   */
  function reposicionar(): void {
    const { estilo, visivel } = useShipStore.getState();
    camadaPerto.setVisible(visivel);
    camadaLonge.setVisible(visivel);
    camadaEscuta.setVisible(visivel && estilo.mostrarEscuta);
    camadaTrilha.setVisible(visivel);
    if (!visivel) {
      fonteFrota.clear();
      return;
    }

    const { displayedTime, currentTime } = useTimeStore.getState();
    const instante = displayedTime ?? currentTime;

    // Esvazia ANTES de mexer nas geometrias: uma feature que ainda está na
    // fonte propaga cada mudança de geometria para o cluster.
    fonteFrota.clear(true);
    const visiveis: Feature[] = [];

    for (const f of todas) {
      const anon = f.get('anonimo') as Anonimo | undefined;
      if (anon) {
        // O ponto anônimo não tem trilha: ele existe só no ciclo publicado.
        visiveis.push(f);
        continue;
      }
      const d = f.get('navio') as NavioFeature | undefined;
      if (!d) continue;
      if (!familiaVisivel(estilo, d.familia)) continue;
      if (d.navio.sumiu && !estilo.mostrarSumidas) continue;

      const pos = posicaoEm(d.navio.trilha, instante);
      if (pos) {
        const [mx, my] = lonLatToMap(pos.lon, pos.lat);
        if (!Number.isFinite(mx) || !Number.isFinite(my)) continue;
        (f.getGeometry() as Point).setCoordinates([mx, my]);
        f.set('pos', { sog: pos.sog, ms: pos.ms }, true);
        f.set('navio', { ...d, instante: pos.ms } satisfies NavioFeature, true);
        visiveis.push(f);
      } else if (instante === null) {
        (f.getGeometry() as Point).setCoordinates([d.navio.mx, d.navio.my]);
        f.set('pos', null, true);
        visiveis.push(f);
      }
      // Sem amostra perto do quadro: não afirmamos onde ela estava.
    }

    // Um único evento de mudança: uma reclusterização, um redesenho.
    fonteFrota.addFeatures(visiveis);
    if (visiveis.length === 0) fonteFrota.changed();
  }

  /** Trilha da embarcação selecionada, até o quadro exibido. */
  function redesenharTrilha(): void {
    fonteTrilha.clear(true);
    const { dados, selecionado, visivel } = useShipStore.getState();
    if (!visivel || selecionado === null) return;

    const navio = dados.navios.find((n) => n.mmsi === selecionado);
    if (!navio) return;

    const { displayedTime, currentTime } = useTimeStore.getState();
    const instante = displayedTime ?? currentTime;

    for (const trecho of trechos(navio.trilha, instante)) {
      const pontos = trecho
        .map((a) => lonLatToMap(a[2], a[1]))
        .filter((p) => Number.isFinite(p[0]) && Number.isFinite(p[1]));
      if (pontos.length >= 2) fonteTrilha.addFeature(new Feature({ geometry: new LineString(pontos) }));
    }
  }

  const pararStore = useShipStore.subscribe((a, b) => {
    if (a.dados !== b.dados) {
      reconstruir();
      return;
    }
    if (a.selecionado !== b.selecionado) redesenharTrilha();
    if (a.visivel !== b.visivel || a.estilo !== b.estilo) {
      reposicionar();
      redesenharTrilha();
    }
  });

  const pararTempo = useTimeStore.subscribe((a, b) => {
    if (a.displayedTime !== b.displayedTime || a.currentTime !== b.currentTime) {
      reposicionar();
      redesenharTrilha();
    }
  });

  reconstruir();

  return {
    camadas: [camadaEscuta, camadaTrilha, camadaLonge, camadaPerto],
    dispose: () => {
      pararStore();
      pararTempo();
      fonteFrota.clear(true);
      fonteEscuta.clear(true);
      fonteTrilha.clear(true);
      todas = [];
    },
  };
}
